/**
 * useNotifications Hook
 *
 * Polls unread count every 60s, provides notification state management.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import {
  getNotifications,
  getUnreadCount,
  markAllAsRead as apiMarkAllAsRead,
  markAsRead as apiMarkAsRead,
  type CustomerNotification,
} from '../services/customerNotificationService';

const POLL_INTERVAL = 60_000; // 60 seconds

export function useNotifications() {
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState<CustomerNotification[]>([]);
  const [loading, setLoading] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | undefined>(undefined);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const count = await getUnreadCount();
      setUnreadCount(count);
    } catch {
      // Silently fail — badge just stays stale
    }
  }, []);

  const fetchNotifications = useCallback(async (limit = 5) => {
    setLoading(true);
    try {
      const result = await getNotifications({ limit });
      setNotifications(result.notifications);
      setUnreadCount(result.unreadCount);
    } catch {
      // Silently fail
    } finally {
      setLoading(false);
    }
  }, []);

  const markAsRead = useCallback(async (notificationId: string) => {
    try {
      await apiMarkAsRead(notificationId);
      setNotifications((prev) =>
        prev.map((n) =>
          n.notificationId === notificationId ? { ...n, read: true } : n,
        ),
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch {
      // Silently fail
    }
  }, []);

  const markAllAsRead = useCallback(async () => {
    try {
      await apiMarkAllAsRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch {
      // Silently fail
    }
  }, []);

  const refresh = useCallback(() => {
    void fetchNotifications(5);
  }, [fetchNotifications]);

  // Start polling on mount
  useEffect(() => {
    void fetchUnreadCount();
    pollRef.current = setInterval(() => {
      void fetchUnreadCount();
    }, POLL_INTERVAL);

    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [fetchUnreadCount]);

  return {
    unreadCount,
    notifications,
    loading,
    markAsRead,
    markAllAsRead,
    refresh,
    fetchNotifications,
  };
}
